import React from 'react';

/**
 * DualRangeSlider - A reusable dual-range slider component
 *
 * @param {number} min - Minimum value for the range
 * @param {number} max - Maximum value for the range
 * @param {number} step - Step increment for values
 * @param {number} lowValue - Current low value
 * @param {number} highValue - Current high value
 * @param {function} onLowChange - Callback when low value changes
 * @param {function} onHighChange - Callback when high value changes
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the slider is disabled
 * @param {boolean} showLabels - Whether to show value labels (default: true)
 * @param {string} label - Optional label for the slider
 */
const DualRangeSlider = ({
  min = 0,
  max = 100,
  step = 1,
  lowValue,
  lowLabel,
  highValue,
  highLabel,
  onLowChange,
  onHighChange,
  className = '',
  disabled = false,
  showLabels = true,
  label = null
}) => {
  // Ensure values are always defined
  const safeLowValue = lowValue ?? min;
  const safeHighValue = highValue ?? max;

  const handleLowChange = (e) => {
    const value = parseInt(e.target.value);
    if (value <= safeHighValue) {
      onLowChange(value);
    }
  };

  const handleHighChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= safeLowValue) {
      onHighChange(value);
    }
  };

  // Calculate the percentage positions for styling
  const lowPercent = ((safeLowValue - min) / (max - min)) * 100;
  const highPercent = ((safeHighValue - min) / (max - min)) * 100;

  return (
    <div className={`${className}`}>
      {/* Optional label */}
      {label && (
        <label className="form-label mb-1 webcam-form-label">
          {label}
        </label>
      )}

      <div
        className="dual-range-container"
        data-low={safeLowValue}
        data-high={safeHighValue}
        style={{
          '--low-percent': `${lowPercent}%`,
          '--high-percent': `${highPercent}%`
        }}
      >
        {/* Track background */}
        <div className="dual-range-track">
          {/* Highlight the selected range in blue */}
          <div
            className="dual-range-track-selected"
            style={{
              left: `${lowPercent}%`,
              width: `${highPercent - lowPercent}%`
            }}
          />
        </div>

        {/* Low value slider */}
        <input
          type="range"
          className="dual-range-input dual-range-input-low"
          min={min}
          max={max}
          step={step}
          value={safeLowValue}
          onChange={handleLowChange}
          disabled={disabled}
        />

        {/* High value slider */}
        <input
          type="range"
          className="dual-range-input dual-range-input-high"
          min={min}
          max={max}
          step={step}
          value={safeHighValue}
          onChange={handleHighChange}
          disabled={disabled}
        />
      </div>
      {/* Value labels */}
      {showLabels && (
        <div className="dual-range-labels">
          <div className="dual-range-threshold-labels">
            <span className="dual-range-threshold-label">
              <small className="text-muted">{lowLabel}</small>
            </span>
            <span className="dual-range-threshold-label">
              <small className="text-muted">{highLabel}</small>
            </span>
          </div>
        </div>

      )}
    </div>
  );
};

export default DualRangeSlider;